import { OFFERWALL_URL, PLATFORM_MESSAGES } from '../constants';
import { createDebugLogger } from './debug';
const debug = createDebugLogger('offerwall');
/**
 * URL flags for in-modal offerwall iframe (legacy `AdpxUtils.replaceFlags`).
 */
export function replaceOfferwallEmbedUrlFlags(url) {
    let out = url;
    if (!/(show_logo=(true|1|yes))/i.test(out)) {
        out += (out.includes('?') ? '&' : '?') + 'show_logo=false';
    }
    if (!/(show_header=(true|1|yes))/i.test(out)) {
        out += (out.includes('?') ? '&' : '?') + 'show_header=false';
    }
    return out
        .replace(/show_logo=(true|1|yes)/gi, 'show_logo=false')
        .replace(/show_header=(true|1|yes)/gi, 'show_header=false')
        .replace(/show_intermediate=(Modal|Interstitial)/gi, 'show_intermediate=false');
}
export function buildOfferwallUrl(apiPayload, settings) {
    const queryStrings = [];
    const accountIdentifier = apiPayload.awinAdvertiserId ||
        apiPayload.accountId;
    if (!accountIdentifier) {
        debug.error('Missing accountId or awinAdvertiserId in payload');
        return '';
    }
    if (apiPayload.awinAdvertiserId) {
        queryStrings.push(`awinAdvertiserId=${encodeURIComponent(String(apiPayload.awinAdvertiserId))}`);
    }
    else {
        queryStrings.push(`accountId=${encodeURIComponent(String(apiPayload.accountId))}`);
    }
    const parentSessionId = apiPayload.sessionId ||
        apiPayload.session_id;
    if (parentSessionId) {
        queryStrings.push(`parent_session_id=${encodeURIComponent(parentSessionId)}`);
    }
    for (const key of Object.keys(apiPayload)) {
        const val = apiPayload[key];
        if (typeof val === 'undefined') {
            continue;
        }
        if (val !== '' && val !== null) {
            if ([
                'accountId',
                'awinAdvertiserId',
                'campaignId',
                'dev',
                'offerId',
                'settings',
                'styles',
                'sessionId',
                'session_id',
            ].includes(key)) {
                continue;
            }
            queryStrings.push(`${key}=${encodeURIComponent(String(val))}`);
        }
    }
    if (settings?.embedded?.enabled) {
        queryStrings.push('suppress_links=true');
    }
    const queryStringPayload = queryStrings.join('&');
    return `${OFFERWALL_URL}?${queryStringPayload}`;
}
const CLOSE_BUTTON_SELECTORS = '#adpx_close-button, #adpx_close-btn, .adpx_close-button';
/**
 * Cloned header is static DOM — OfferHeader's close-delay timer does not run on it.
 * Always enable close in the offerwall overlay (delay applies to the offer flow only).
 */
function enableClonedHeaderCloseButton(btn) {
    btn.removeAttribute('disabled');
    if (btn instanceof HTMLButtonElement) {
        btn.disabled = false;
    }
    btn.style.opacity = '1';
    btn.style.pointerEvents = 'auto';
}
function wireCloseOnClonedHeader(headerRoot, closeModal) {
    const btn = headerRoot.querySelector(CLOSE_BUTTON_SELECTORS);
    if (btn) {
        enableClonedHeaderCloseButton(btn);
        btn.onclick = e => {
            e.preventDefault();
            e.stopPropagation();
            closeModal();
        };
    }
}
function injectOfferwallOverlayForEmbedded(root, url, styles) {
    const existing = root.getElementById('adpx_offerwall-embed-overlay');
    if (existing) {
        return;
    }
    const overlay = document.createElement('div');
    overlay.id = 'adpx_offerwall-embed-overlay';
    overlay.style.cssText = [
        'position:fixed',
        'top:0',
        'left:0',
        'right:0',
        'bottom:0',
        'z-index:2147483647',
        'background:rgba(0,0,0,0.5)',
        'display:flex',
        'align-items:center',
        'justify-content:center',
    ].join(';');
    const panel = document.createElement('div');
    panel.style.cssText = [
        'position:relative',
        'width:100%',
        `max-width:${window.innerWidth > 640 ? '80%' : '100%'}`,
        `height:${Math.round(window.innerHeight * 0.9)}px`,
        `background:${styles.popup?.background || '#ffffff'}`,
        'border-radius:8px',
        'overflow:hidden',
        'display:flex',
        'flex-direction:column',
    ].join(';');
    const closeBtn = document.createElement('button');
    closeBtn.innerHTML = '&times;';
    closeBtn.style.cssText = [
        'position:absolute',
        'top:8px',
        'right:10px',
        'border:none',
        'border-radius:4px',
        'color:#000',
        'font-size:24px',
        'background: transparent',
        'line-height:1',
        'width:32px',
        'height:32px',
        'cursor:pointer',
        'z-index:1',
        'display:flex',
        'align-items:center',
        'justify-content:center',
    ].join(';');
    closeBtn.onclick = () => overlay.remove();
    const iframe = document.createElement('iframe');
    iframe.src = replaceOfferwallEmbedUrlFlags(url);
    iframe.setAttribute('frameBorder', '0');
    iframe.setAttribute('scrolling', 'auto');
    iframe.style.cssText = 'width:100%;flex:1;min-height:0;border:none;';
    panel.appendChild(closeBtn);
    panel.appendChild(iframe);
    overlay.appendChild(panel);
    if (root instanceof ShadowRoot) {
        root.appendChild(overlay);
    }
    else {
        document.body.appendChild(overlay);
    }
}
function injectOfferwallIframeIntoPopup(root, url, styles, closeModal) {
    const modal = root.getElementById('adpx_popup');
    if (!modal) {
        window.open(replaceOfferwallEmbedUrlFlags(url), '_blank');
        return;
    }
    const headerEl = root.getElementById('adpx_modal-header');
    const footerEl = root.getElementById('adpx_footer-text-container') ||
        root.getElementById('adpx_mou-footer-text-container');
    const headerClone = headerEl?.cloneNode(true);
    const footerClone = footerEl?.cloneNode(true);
    if (headerClone) {
        wireCloseOnClonedHeader(headerClone, closeModal);
    }
    modal.innerHTML = '';
    const viewportHeight = window.innerHeight;
    modal.style.width = '90%';
    modal.style.maxWidth = window.innerWidth > 640 ? '80%' : '100%';
    modal.style.height = '90%';
    modal.style.maxHeight = `${viewportHeight * 0.9}px`;
    modal.style.minWidth = '90%';
    modal.style.minHeight = '90%';
    modal.style.overflow = 'hidden';
    modal.style.background = styles.popup?.background || '#fff';
    modal.style.display = 'flex';
    modal.style.flexDirection = 'column';
    const iframe = document.createElement('iframe');
    iframe.id = 'adpx_offerwall_iframe';
    iframe.src = replaceOfferwallEmbedUrlFlags(url);
    iframe.setAttribute('frameBorder', '0');
    iframe.setAttribute('scrolling', 'auto');
    iframe.style.width = '100%';
    iframe.style.flex = '1';
    iframe.style.minHeight = '0';
    iframe.style.border = 'none';
    if (headerClone) {
        modal.appendChild(headerClone);
    }
    modal.appendChild(iframe);
    if (footerClone) {
        modal.appendChild(footerClone);
    }
}
/**
 * Opens the Perkswall offerwall like legacy `openOfferWall`:
 * - `open_overlay_offerwall` false → new tab
 * - webview + overlay → parent `url_clicked` with `external`
 * - close button + overlay → new tab (with URL flags), not iframe swap
 * - otherwise → replace `#adpx_popup` body with header + iframe + footer clones
 */
export function openPublisherOfferwall(options) {
    const { settings, styles, apiPayload, root, fromCloseButton, isWebView, sendMessage, closeModal, } = options;
    const url = buildOfferwallUrl(apiPayload, settings);
    if (!url) {
        debug.error('Failed to build offerwall URL');
        return;
    }
    if (!settings.open_overlay_offerwall) {
        window.open(url, '_blank');
        return;
    }
    if (isWebView) {
        sendMessage(PLATFORM_MESSAGES.URL_CLICKED.name, {
            ...PLATFORM_MESSAGES.URL_CLICKED,
            target_url: url,
            external: settings.open_overlay_offerwall !== true,
        });
        return;
    }
    if (settings.embedded?.enabled) {
        injectOfferwallOverlayForEmbedded(root, url, styles);
        return;
    }
    if (fromCloseButton) {
        window.open(replaceOfferwallEmbedUrlFlags(url), '_blank');
        return;
    }
    injectOfferwallIframeIntoPopup(root, url, styles, closeModal);
}
